import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import {
  DOSSIER_FACT_ENUM_VALUES,
  PROPERTY_FIELD_ENUM_FACT_KEYS,
  MEMORY_SOURCE_TYPES,
  type AnalysisConfidence,
  type MemorySourceType,
  type OperatorMemoryEntry,
  type OperatorPropertyMemory,
} from '@immo/api-contract';
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import { FilterChip } from '@/components/filter-chip';
import { FilterBar, FilterPill } from '@/components/filter-bar';
import { ARIA_SORT, SortableHeader, useTableSort, type SortValue } from '@/components/sortable-table';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { useDossierValueFormatter } from '@/pages/properties/dossier-value';
import { MemoryDocumentViewer } from '@/pages/properties/memory-document-viewer';

const NS = 'pages.admin.properties.detail.memory';

const CONFIDENCE_RANK: Record<AnalysisConfidence, number> = { LOW: 0, MEDIUM: 1, HIGH: 2 };

const FIELDS = [
  'label',
  'value',
  'correction',
  'source',
  'passage',
  'prompt',
  'model',
  'confidence',
  'readAt',
] as const;

type Field = (typeof FIELDS)[number];

const COLUMN_KEY: Record<Field, string> = {
  label: 'label',
  value: 'value',
  correction: 'correction',
  source: 'source',
  passage: 'passage',
  prompt: 'prompt',
  model: 'model',
  confidence: 'confidence',
  readAt: 'read-at',
};

/**
 * The Memory tab's fetch state. Every state past `idle` names the Objekt it was
 * fetched for, so a page that outlives a property change never shows the previous
 * Objekt's memory. A permission refusal (`forbidden`) is distinct from a transient
 * failure (`failed`): only the latter offers a retry.
 */
export type MemoryState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading'; readonly propertyId: string }
  | { readonly status: 'loaded'; readonly propertyId: string; readonly memory: OperatorPropertyMemory }
  | { readonly status: 'forbidden'; readonly propertyId: string }
  | { readonly status: 'failed'; readonly propertyId: string };

export type MemoryController = {
  readonly state: MemoryState;
  /** Called when the tab is activated. Loads only when nothing was fetched for this Objekt yet. */
  readonly open: () => void;
  /** Forces a refetch after a transient failure. */
  readonly retry: () => void;
};

/** Fetch-once-on-open, lifted into the detail page so switching tabs away and back issues no second request. */
export function useMemoryController(propertyId: string): MemoryController {
  const [state, setState] = useState<MemoryState>({ status: 'idle' });
  const inFlightFor = useRef<string | null>(null);
  const currentPropertyId = useRef(propertyId);

  currentPropertyId.current = propertyId;

  const load = useCallback(async () => {
    if (inFlightFor.current === propertyId) return;

    inFlightFor.current = propertyId;
    setState({ status: 'loading', propertyId });

    try {
      const result = await apiClient.adminProperties.memory({ pathParams: { id: propertyId } });

      if (currentPropertyId.current !== propertyId) return;

      if (result.ok) {
        setState({ status: 'loaded', propertyId, memory: result.body });
      } else if (result.status === 403) {
        setState({ status: 'forbidden', propertyId });
      } else {
        setState({ status: 'failed', propertyId });
      }
    } catch {
      if (currentPropertyId.current === propertyId) setState({ status: 'failed', propertyId });
    } finally {
      if (inFlightFor.current === propertyId) inFlightFor.current = null;
    }
  }, [propertyId]);

  const open = useCallback(() => {
    if (state.status !== 'idle' && state.propertyId === propertyId) return;

    void load();
  }, [state, propertyId, load]);

  const retry = useCallback(() => {
    void load();
  }, [load]);

  return useMemo(() => ({ state, open, retry }), [state, open, retry]);
}

type MemoryTabProps = {
  readonly propertyId: string;
  readonly state: MemoryState;
  readonly onRetry: () => void;
};

/** An Objekt's memory, read-only: every statement immoOS read, with where and how it was read, and every statement an operator added. */
export function MemoryTab({ propertyId, state, onRetry }: MemoryTabProps): ReactNode {
  const t = useTranslations(NS);

  if (state.status === 'idle' || state.propertyId !== propertyId || state.status === 'loading') {
    return <p className="py-10 text-center text-base-sm text-text-tertiary">{t('loading')}</p>;
  }

  if (state.status === 'forbidden') {
    return (
      <div className="rounded-md border border-neutral-200 bg-canvas px-4 py-10 text-center text-base-sm text-text-secondary">
        {t('forbidden')}
      </div>
    );
  }

  if (state.status === 'failed') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-md border border-neutral-200 bg-canvas px-4 py-10 text-center">
        <p className="text-base-sm text-text-secondary">{t('error')}</p>
        <Button variant="secondary" onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  return <MemoryView propertyId={propertyId} memory={state.memory} />;
}

/**
 * ONE renderer for `value` and `correction`, so the two columns cannot drift
 * apart (BL-082). Returns '' for an empty value.
 */
function useRenderMemoryValue(): (fieldKey: string | null, value: unknown) => string {
  const intl = useIntl();
  const formatValue = useDossierValueFormatter();

  return useCallback(
    (fieldKey, value) => {
      if (value === null || value === undefined) return '';

      const enumFactKey =
        fieldKey !== null && Object.hasOwn(PROPERTY_FIELD_ENUM_FACT_KEYS, fieldKey)
          ? PROPERTY_FIELD_ENUM_FACT_KEYS[fieldKey as keyof typeof PROPERTY_FIELD_ENUM_FACT_KEYS]
          : null;

      if (
        enumFactKey !== null &&
        typeof value === 'string' &&
        (DOSSIER_FACT_ENUM_VALUES[enumFactKey] as readonly string[]).includes(value)
      ) {
        return formatValue({ factKey: enumFactKey, valueType: 'enum', numberFormat: null }, value);
      }

      if (typeof value === 'number') return intl.formatNumber(value);

      if (typeof value === 'string') return value;

      return JSON.stringify(value);
    },
    [intl, formatValue],
  );
}

function MemoryView({ propertyId, memory }: { propertyId: string; memory: OperatorPropertyMemory }): ReactNode {
  const t = useTranslations(NS);
  const intl = useIntl();
  const renderValue = useRenderMemoryValue();
  const [filter, setFilter] = useState<MemorySourceType | null>(null);
  const [viewerDocument, setViewerDocument] = useState<{ id: string; name: string } | null>(null);

  const filtered = useMemo(
    () => (filter === null ? memory.entries : memory.entries.filter((entry) => entry.source.type === filter)),
    [memory.entries, filter],
  );

  const sortKey = useCallback(
    (entry: OperatorMemoryEntry, field: Field): SortValue => {
      switch (field) {
        case 'label':
          return entry.label;
        case 'value':
          return valueSortKey(entry.value, renderValue(entry.fieldKey, entry.value));
        case 'correction':
          return entry.correction === null
            ? null
            : valueSortKey(entry.correction.value, renderValue(entry.fieldKey, entry.correction.value));
        case 'source':
          return `${t(`source-type.${entry.source.type}`)} ${entry.source.name ?? ''}`.trim();
        case 'passage':
          return passageSortKey(entry.passage);
        case 'prompt':
          return entry.prompt === null ? null : promptLabel(entry);
        case 'model':
          return entry.model;
        case 'confidence':
          return CONFIDENCE_RANK[entry.confidence];
        case 'readAt':
          return Date.parse(entry.readAt);
      }
    },
    [renderValue, t],
  );

  const { sorted, sort, toggleSort } = useTableSort(filtered, sortKey);

  if (memory.entries.length === 0) {
    return <p className="py-8 text-center text-base-sm text-text-tertiary">{t('empty')}</p>;
  }

  return (
    <div className="space-y-3">
      <FilterBar
        pills={
          <>
            <span className="text-base-sm text-text-secondary">{t('filter.label')}</span>
            {MEMORY_SOURCE_TYPES.map((type) => (
              <FilterPill
                key={type}
                label={t(`source-type.${type}`)}
                active={filter === type}
                onToggle={() => setFilter(filter === type ? null : type)}
              />
            ))}
          </>
        }
      />
      {filter !== null && (
        <div>
          <FilterChip
            label={t('filter.chip', { type: t(`source-type.${filter}`) })}
            removeLabel={t('filter.remove')}
            onRemove={() => setFilter(null)}
          />
        </div>
      )}
      {sorted.length === 0 ? (
        <p className="py-8 text-center text-base-sm text-text-tertiary">{t('empty')}</p>
      ) : (
        <Table scrollX>
          <TableHeader>
            <TableRow>
              {FIELDS.map((field) => (
                <TableHead
                  key={field}
                  {...(sort !== null && sort.field === field ? { 'aria-sort': ARIA_SORT[sort.direction] } : {})}
                >
                  <SortableHeader
                    field={field}
                    label={t(`columns.${COLUMN_KEY[field]}`)}
                    ariaLabel={t('sort-label', { column: t(`columns.${COLUMN_KEY[field]}`) })}
                    sort={sort}
                    onSort={toggleSort}
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((entry) => {
              const orphaned = entry.operator.orphanedAt !== null;
              const primary = orphaned ? 'text-text-tertiary' : 'text-text-primary';
              const secondary = orphaned ? 'text-text-tertiary' : 'text-text-secondary';

              return (
                <TableRow key={entry.id}>
                  <TableCell className={primary}>
                    <span className="block">{entry.label}</span>
                    {entry.operator.addedBy !== null && (
                      <span className={`block text-xs ${secondary}`}>
                        {t('operator.added', {
                          name: entry.operator.addedBy.name ?? t('operator.unknown'),
                          date: intl.formatDate(entry.readAt, { dateStyle: 'medium', timeStyle: 'short' }),
                        })}
                      </span>
                    )}
                    {entry.operator.edited !== null && (
                      <span className={`block text-xs ${secondary}`}>
                        {t('operator.edited', {
                          name: entry.operator.edited.by.name ?? t('operator.unknown'),
                          date: intl.formatDate(entry.operator.edited.at, { dateStyle: 'medium', timeStyle: 'short' }),
                        })}
                      </span>
                    )}
                    {orphaned && <span className="block text-xs">{t('operator.orphaned')}</span>}
                    {entry.operator.deleted !== null && (
                      <span className="block text-xs">
                        {t('operator.deleted', {
                          name: entry.operator.deleted.by.name ?? t('operator.unknown'),
                          date: intl.formatDate(entry.operator.deleted.at, { dateStyle: 'medium', timeStyle: 'short' }),
                        })}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className={primary}>{renderValue(entry.fieldKey, entry.value)}</TableCell>
                  <TableCell className={primary}>
                    {entry.correction !== null && (
                      <>
                        <span className="block">{renderValue(entry.fieldKey, entry.correction.value)}</span>
                        {entry.operator.correctedBy !== null && (
                          <span className={`block text-xs ${secondary}`}>
                            {t('operator.by', {
                              name: entry.operator.correctedBy.name ?? t('operator.unknown'),
                              date: intl.formatDate(entry.correction.correctedAt, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              }),
                            })}
                          </span>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell className={secondary}>
                    <span className="block">{t(`source-type.${entry.source.type}`)}</span>
                    <SourceName entry={entry} onOpenDocument={setViewerDocument} />
                  </TableCell>
                  <TableCell className={secondary}>
                    {entry.passage.page !== null && (
                      <span className="block">{t('page', { page: entry.passage.page })}</span>
                    )}
                    {entry.passage.excerpt !== null && (
                      <span className="block max-w-xs whitespace-normal">{entry.passage.excerpt}</span>
                    )}
                  </TableCell>
                  <TableCell className={secondary}>{promptLabel(entry)}</TableCell>
                  <TableCell className={secondary}>{entry.model ?? ''}</TableCell>
                  <TableCell className={secondary}>{t(`confidence.${entry.confidence}`)}</TableCell>
                  <TableCell className={secondary}>
                    {intl.formatDate(entry.readAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <MemoryDocumentViewer propertyId={propertyId} document={viewerDocument} onClose={() => setViewerDocument(null)} />
    </div>
  );
}

function SourceName({
  entry,
  onOpenDocument,
}: {
  entry: OperatorMemoryEntry;
  onOpenDocument: (document: { id: string; name: string }) => void;
}): ReactNode {
  const { type, id, name } = entry.source;

  if (name === null) return null;

  if (type !== 'DOCUMENT') return <span className="block text-xs">{name}</span>;

  return (
    <button
      type="button"
      onClick={() => onOpenDocument({ id, name })}
      className="block text-left text-xs text-interactive hover:underline"
    >
      {name}
    </button>
  );
}

function promptLabel(entry: OperatorMemoryEntry): string {
  if (entry.prompt === null) return '';

  return `${entry.prompt.key} · v${entry.prompt.version}`;
}

/** A passage with only an excerpt is not an empty cell: pages first (zero-padded), then excerpt-only, empty last. */
function passageSortKey(passage: OperatorMemoryEntry['passage']): SortValue {
  if (passage.page !== null) return `0 ${String(passage.page).padStart(6, '0')} ${passage.excerpt ?? ''}`.trim();

  return passage.excerpt !== null && passage.excerpt !== '' ? `1 ${passage.excerpt}` : null;
}

function valueSortKey(value: unknown, rendered: string): SortValue {
  if (typeof value === 'number') return value;

  return rendered === '' ? null : rendered;
}
